import React, { useState } from "react";
import { appRegisterUser, appSendRegisterOtp } from "../../services/AuthServices";
import {
  ErrorsMsg,
  PLEASE_WAIT_TXT,
  REGEX_MOBILE_NUMBER,
} from "../../utils/Constant";
import FacebookAuth from "./FacebookAuth";
import GoogleAuth from "./GoogleAuth";
import { FloatingLabel, Form } from "react-bootstrap";
import { Formik } from "formik";
import * as yup from "yup";
import {isMacOs, isIOS} from "react-device-detect";
import AppleAuth from "./AppleAuth";
import punycode from "punycode";


/**
 * BOC
 * Task#140069-Order number 100145548 issue in order status
 * author Maanik Arya
 * date 19-Jun-2023
 * use_of_code: This code is used to validate email address. We are using punycode to convert email address to unicode and then validating it.
 */
const schema = yup.object().shape({
  first_name: yup.string().trim().required(ErrorsMsg.required),
  email: yup.string().trim().required(ErrorsMsg.required).email(ErrorsMsg.validEmail)
  .test('validEmail', ErrorsMsg.validEmail, (value) => {
    if(value){
      const unicodeEmail = punycode.toUnicode(value.trim());
      const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const result =  emailRegex.test(unicodeEmail);
      return result;
    }
    return false;
  }),
  mobile_number: yup
    .string()
    .trim()
    .required(ErrorsMsg.required)
    .matches(REGEX_MOBILE_NUMBER, ErrorsMsg.validPhone),
});

/**
 * BOC
 * API Security - Step 4 OTP login / signup UI
 * date 23-10-2026
 * use_of_code: Signup without password. E-mail and mobile number are checked, then the registration OTP is sent
 * (same OTP on SMS and e-mail) and the OTP screen is opened. The account is created on the OTP screen.
 */
function Signup({ active, toggleAuthFormCb, setSignupOTPResponse }) {
  const [msg, setMsg] = useState("");

  if (!active) return null;

  const onSubmitSuccess = (
    values,
    { setSubmitting, setFieldError }
  ) => {
    setMsg("");
    let signupData = {
      is_validate: 1,
      title: "",
      first_name: values.first_name.trim(),
      last_name: "",
      mobile_number: values.mobile_number.trim(),
      country_code: "+91",
      email: values.email.trim(),
      dob: "",
      user_type: "",
      social_login: "No",
      is_seller: "0",
      request_storefront: "IN", // Added by Anurag Chauhan on 09-05-2024 for Task# #149375 - Add request_storefront in signup API.
    };
    const onError = () => {
      setMsg("Something went wrong. Please try again.");
      setSubmitting(false);
    };
    // validate email if already exists
    appRegisterUser(signupData).then((res) => {
      res = JSON.parse(res?.data);

      if (res.signup_user?.status === "success") {
        // validate mobile if already exists
        appRegisterUser({ ...signupData, is_validate: 2 }).then((res2) => {
          res2 = JSON.parse(res2?.data);
          if (res2.signup_user?.status === "success") {
            // send the registration OTP
            appSendRegisterOtp({
              first_name: signupData.first_name,
              email: signupData.email,
              mobile_number: signupData.mobile_number,
            }).then((res3) => {
              res3 = JSON.parse(res3?.data);
              if (res3?.status === "success") {
                setSignupOTPResponse(res3, { ...signupData, is_validate: 0 });
                toggleAuthFormCb("signupotp");
              } else if (res3?.error_code === "EMAIL_EXISTS") {
                setFieldError("email", res3?.message || "");
                setSubmitting(false);
              } else if (res3?.error_code === "MOBILE_EXISTS") {
                setFieldError("mobile_number", res3?.message || "");
                setSubmitting(false);
              } else {
                setMsg(res3?.message || "Something went wrong. Please try again.");
                setSubmitting(false);
              }
            }).catch(onError);
          } else {
            // mobile already exists
            setFieldError("mobile_number", res2.signup_user?.message || "");
            setSubmitting(false);
          }
        }).catch(onError);
      } else {
        // email already exists
        setFieldError("email", res.signup_user?.message || "");
        setSubmitting(false);
      }
    }).catch(onError);
  };

  return (
    <>
      <Formik
        validationSchema={schema}
        onSubmit={onSubmitSuccess}
        initialValues={{
          first_name: "",
          email: "",
          mobile_number: "",
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FloatingLabel controlId="first_name" label="Name*" className="mb-3">
              <Form.Control
                type="text"
                name="first_name"
                autoComplete="off"
                placeholder="Name"
                value={values.first_name}
                onChange={handleChange}
                isInvalid={touched.first_name && errors.first_name}
              />

              <Form.Control.Feedback type="invalid">
                {errors.first_name}
              </Form.Control.Feedback>
            </FloatingLabel>

            <FloatingLabel
              controlId="email"
              label="Email address*"
              className="mb-3"
            >
              <Form.Control
                type="email"
                name="email"
                autoComplete="off"
                placeholder="Email address"
                value={values.email}
                onChange={handleChange}
                isInvalid={touched.email && errors.email}
              />

              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </FloatingLabel>

            <FloatingLabel
              controlId="mobile_number"
              label="Mobile number*"
              className="mb-3"
            >
              <Form.Control
                type="text"
                inputMode="numeric"
                maxLength={10}
                name="mobile_number"
                autoComplete="off"
                placeholder="Mobile number"
                value={values.mobile_number}
                onChange={handleChange}
                isInvalid={touched.mobile_number && errors.mobile_number}
              />

              <Form.Control.Feedback type="invalid">
                {errors.mobile_number}
              </Form.Control.Feedback>
            </FloatingLabel>

            {msg.length > 0 ? <span className="text-danger">{msg}</span> : null}

            <button
              className="btn btn-primary mt-35"
              type="submit"
              disabled={isSubmitting || !values.first_name || !values.email || !values.mobile_number}
            >
              {isSubmitting ? PLEASE_WAIT_TXT : "SIGN UP WITH OTP"}
            </button>
          </Form>
        )}
      </Formik>

      <div className="social-login mt-55">
        <p>or sign up with</p>
        <ul className="mt-55 login-item-list">
          <li>
            <FacebookAuth />
          </li>
          <li>
            <GoogleAuth />
          </li>
          {(isMacOs || isIOS) ? <li><AppleAuth/></li> : ""}
        </ul>
        <button
          onClick={(e) => toggleAuthFormCb("login")}
          className="btn-blank text-underline mt-35 hv-acc-btn"
          type="button"
        >
          Already have an account<span className="questionmark">?</span>
        </button>
      </div>
    </>
  );
}
/**
 * EOC
 */

export default Signup;
